/**
 * Registre des écrans AGORA : id du nœud du catalogue (packages/shared/src/catalogue.ts) → écran (chargé à la demande).
 *
 * Ajouter l'écran d'un module / sous-module :
 *   1. le déclarer dans le catalogue (statut 'disponible') + libellés `catalogue.<id>` dans les 6 langues ;
 *   2. créer l'écran dans web/src/metiers/<metier>/<module>/… (props : EcranProps) en n'important que
 *      '../../design' (composants) et '../../socle' (useT, useDroits, useApi, useFiliale, useFormats, LienPage…) ;
 *   3. l'enregistrer ci-dessous.
 * Sans entrée ici, la route affiche le squelette « Écran à développer » avec les droits de l'utilisateur.
 * Un écran ne s'affiche que si l'utilisateur a le droit `lire` sur le nœud (contrôlé par le socle).
 *
 * Fiches d'objet (ticket, OT, tiers…) : une route sous celle du nœud (`/referentiels/tiers/:id`) déclarée dans FICHES ;
 * mêmes contrôles (nœud activé et lisible). Depuis un autre écran, y rebondir avec <LienPage> (ouvrir dans une page).
 */
import React from 'react';
import {
  CLE_PJ_INCIDENT, RESSOURCE_AFFAIRES, RESSOURCE_PISTES, type Action, type AnalyseMessage, type CleTraduction, type ContactAppel, type Message,
  type NoeudCatalogue, type PieceJointe, type RessourceLieeAppel, type TiersAppel,
} from '@agora/shared';
import type { IconName } from '../design';
import type { FichierJoint } from '../socle';

export interface EcranProps {
  /** Nœud du catalogue (id, route, droits de l'utilisateur pour la filiale courante, enfants lisibles). */
  noeud: NoeudCatalogue;
}

type Ecran = React.LazyExoticComponent<React.ComponentType<EcranProps>>;

export const ECRANS: Record<string, Ecran> = {
  // Référentiels (tables de dimension transverses)
  tiers: React.lazy(() => import('./referentiels/Tiers')),
  contacts: React.lazy(() => import('./referentiels/Contacts')),
  filiales: React.lazy(() => import('./referentiels/Filiales')),
  batiments: React.lazy(() => import('./referentiels/Batiments')),
  // Service › Téléphonie (POC)
  'service.telephonie.appels': React.lazy(() => import('./service/telephonie/Appels')),
  // Commerce › Catalogue
  'commerce.catalogue.solutions': React.lazy(() => import('./commerce/catalogue/Solutions')),
  'commerce.catalogue.tarifs': React.lazy(() => import('./commerce/catalogue/Tarifs')),
  'commerce.vente.affaires': React.lazy(() => import('./commerce/vente/Affaires')),
  'commerce.vente.pipeline': React.lazy(() => import('./commerce/vente/Pipeline')),
  'commerce.vente.devis': React.lazy(() => import('./commerce/vente/Devis')),
  'commerce.vente.validations': React.lazy(() => import('./commerce/vente/Validations')),
  'commerce.vente.passations': React.lazy(() => import('./commerce/vente/Passations')),
  'commerce.catalogue.delegations': React.lazy(() => import('./commerce/catalogue/Delegations')),
  'commerce.prospection.pistes': React.lazy(() => import('./commerce/prospection/Pistes')),
  'commerce.prospection.concurrents': React.lazy(() => import('./commerce/prospection/Concurrents')),
  // IT › Demandes & incidents
  'it.demandes.incidents': React.lazy(() => import('./it/demandes/Incidents')),
  // Outils personnels du socle › Messagerie (boîte Microsoft 365 de l'utilisateur)
  'outils.messagerie': React.lazy(() => import('./outils/messagerie/Messagerie')),
  'outils.trousseau': React.lazy(() => import('./outils/trousseau/Trousseau')),
  // Scribe : mes réunions enregistrées (l'enregistrement se fait dans le panneau Scribe du dock).
  'outils.scribe': React.lazy(() => import('./outils/scribe/Scribe')),
  // Exemple : 'service.hotline.tickets': React.lazy(() => import('./service/hotline/Tickets')),
};

export interface Fiche {
  /** Route React Router (paramètres lus par l'écran avec useParams). */
  chemin: string;
  /** Nœud du catalogue qui porte la fiche (activation, droits, fil d'Ariane). */
  noeud: string;
  ecran: Ecran;
}

export const FICHES: Fiche[] = [
  { chemin: '/referentiels/tiers/:id', noeud: 'tiers', ecran: React.lazy(() => import('./referentiels/FicheTiers')) },
  { chemin: '/service/telephonie/appels/:id', noeud: 'service.telephonie.appels', ecran: React.lazy(() => import('./service/telephonie/FicheAppel')) },
  { chemin: '/referentiels/contacts/:id', noeud: 'contacts', ecran: React.lazy(() => import('./referentiels/FicheContact')) },
  { chemin: '/commerce/catalogue/solutions/:id', noeud: 'commerce.catalogue.solutions', ecran: React.lazy(() => import('./commerce/catalogue/FicheSolution')) },
  { chemin: '/commerce/catalogue/tarifs/:id', noeud: 'commerce.catalogue.tarifs', ecran: React.lazy(() => import('./commerce/catalogue/FicheTarif')) },
  { chemin: '/commerce/vente/affaires/:id', noeud: 'commerce.vente.affaires', ecran: React.lazy(() => import('./commerce/vente/FicheAffaire')) },
  { chemin: '/commerce/vente/devis/:id', noeud: 'commerce.vente.devis', ecran: React.lazy(() => import('./commerce/vente/FicheDevis')) },
  { chemin: '/commerce/prospection/pistes/:id', noeud: 'commerce.prospection.pistes', ecran: React.lazy(() => import('./commerce/prospection/FichePiste')) },
  { chemin: '/commerce/prospection/concurrents/:id', noeud: 'commerce.prospection.concurrents', ecran: React.lazy(() => import('./commerce/prospection/FicheConcurrent')) },
  // Signatures e-mail PRODEVAL de la messagerie (droit « administrer » : marketing ; menu Administration).
  { chemin: '/outils/messagerie/signatures', noeud: 'outils.messagerie', ecran: React.lazy(() => import('./outils/messagerie/Signatures')) },
  // Réunion enregistrée par Scribe : compte-rendu à relire puis valider (aussi la cible de la notification de fin du traitement).
  { chemin: '/outils/scribe/:id', noeud: 'outils.scribe', ecran: React.lazy(() => import('./outils/scribe/FicheReunion')) },
  // Référentiels du support (droit « administrer ») : chemin fixe, prioritaire sur :id pour React Router.
  { chemin: '/it/demandes/incidents/referentiels', noeud: 'it.demandes.incidents', ecran: React.lazy(() => import('./it/demandes/Referentiels')) },
  // Aussi la cible des notifications des incidents (lien /it/demandes/incidents/<id>).
  { chemin: '/it/demandes/incidents/:id', noeud: 'it.demandes.incidents', ecran: React.lazy(() => import('./it/demandes/FicheIncident')) },
  // Exemple : { chemin: '/service/hotline/tickets/:id', noeud: 'service.hotline.tickets', ecran: React.lazy(() => import('./service/hotline/FicheTicket')) },
];

/**
 * Flux de la messagerie (Outils › Messagerie, encadré « Actions IA » d'un e-mail ouvert) : l'e-mail enregistré dans un
 * module – incident prérempli, piste, rattachement à une affaire… Un flux s'affiche si l'utilisateur a le droit
 * `action` sur `noeud` dans la filiale de l'en-tête (jamais en vue consolidée, en lecture seule). Le formulaire du flux
 * (props : FluxMessageProps) n'importe, comme un écran, que '../../design' et '../../socle'.
 *  - `depot` : clé des pièces jointes (dépôt différé) du formulaire de création ; l'e-mail (.eml) y est déposé avant
 *    l'ouverture du formulaire (prop `email`, à joindre à la création avec idsDepots) ;
 *  - sans `depot` : le flux joint lui-même l'e-mail à un objet existant (`rattacherEmail`).
 */
export interface FluxMessageProps {
  /** E-mail ouvert (objet, expéditeur, destinataires, dates). */
  message: Message;
  /** Corps de l'e-mail en texte brut (préremplissage). */
  texte: string;
  /** Filiale précise de l'en-tête. */
  filiale: string;
  /** Correspondant analysé (contact connu, entreprises trouvées, proposition) si l'utilisateur lit les contacts. */
  analyse?: AnalyseMessage;
  /** L'e-mail déposé, prêt (flux avec `depot`). */
  email?: FichierJoint;
  /** L'e-mail joint tout de suite à un objet existant (clé des pièces jointes en dépôt direct). */
  rattacherEmail: (ressource: string, objetId: string) => Promise<PieceJointe>;
  /** Fin du flux : abandon (sans argument), ou enregistrement – confirmation affichée par la messagerie, avec un lien
   *  vers l'objet. */
  fermer: (fait?: FluxTermine) => void;
}

export interface FluxTermine {
  texte: string;
  lien?: { to: string; label: string };
}

export interface FluxMessage {
  id: string;
  noeud: string;
  action: Action;
  /** Clé i18n du bouton. */
  libelle: CleTraduction;
  icone: IconName;
  depot?: string;
  composant: React.LazyExoticComponent<React.ComponentType<FluxMessageProps>>;
}

export const FLUX_MESSAGE: FluxMessage[] = [
  // Référentiels › Tiers : l'e-mail joint à la fiche d'une entreprise (contact connu, domaine, recherche).
  {
    id: 'tiers', noeud: 'tiers', action: 'modifier', libelle: 'outils.messagerie.flux.tiers', icone: 'building',
    composant: React.lazy(() => import('./referentiels/EmailSurTiers')),
  },
  // Commerce › Pistes : piste préremplie (objet, entreprise et contact du correspondant, besoin = texte de l'e-mail).
  {
    id: 'piste', noeud: RESSOURCE_PISTES, action: 'creer', libelle: 'outils.messagerie.flux.piste', icone: 'pin',
    composant: React.lazy(() => import('./commerce/prospection/PisteDepuisMessage')),
  },
  // Commerce › Affaires : l'e-mail joint à une affaire (affaires en cours du client, ou recherche).
  {
    id: 'affaire', noeud: RESSOURCE_AFFAIRES, action: 'modifier', libelle: 'outils.messagerie.flux.affaire', icone: 'portefeuille',
    composant: React.lazy(() => import('./commerce/vente/EmailSurAffaire')),
  },
  // IT › Incidents : déclaration préremplie (objet, texte, demandeur PRODEVAL), l'e-mail en pièce jointe.
  {
    id: 'incident', noeud: 'it.demandes.incidents', action: 'creer', libelle: 'outils.messagerie.flux.incident', icone: 'ticket',
    depot: CLE_PJ_INCIDENT, composant: React.lazy(() => import('./it/demandes/IncidentDepuisMessage')),
  },
];

/**
 * Flux du softphone (panneau Téléphone, menu « Affecter » d'un appel) : un objet du module créé depuis l'appel en cours
 * ou tout juste terminé – formulaire prérempli avec le correspondant reconnu (contact, tiers) et le numéro – puis
 * l'appel lui est affecté. Proposé si l'API l'autorise (droit « creer », module actif : GET /v1/service/appels/propositions,
 * sortes du poste de l'utilisateur en premier) et que le flux en a besoin (`tiersRequis` : correspondant reconnu).
 * Le formulaire (props : FluxAppelProps) n'importe, comme un écran, que '../../design' et '../../socle'.
 */
export interface FluxAppelProps {
  /** Filiale du softphone (précise). */
  filiale: string;
  /** Numéro du correspondant (E.164), null si masqué. */
  numero: string | null;
  /** Correspondant reconnu par son numéro. */
  tiers: TiersAppel | null;
  contact: ContactAppel | null;
  /** Origine écrite dans l'objet (« Appel du … avec … ») : description, besoin. */
  origine: string;
  /** Fin du flux : abandon (sans argument) ou objet créé (affecté ensuite à l'appel). */
  fermer: (cree?: { id: string; libelle: string }) => void;
}

export interface FluxAppel {
  ressource: RessourceLieeAppel;
  /** Clé i18n de l'entrée du menu (« Nouvelle piste »…). */
  libelle: CleTraduction;
  icone: IconName;
  /** Le formulaire du module exige un tiers (contact, affaire) : flux proposé seulement si le correspondant est reconnu. */
  tiersRequis?: boolean;
  composant: React.LazyExoticComponent<React.ComponentType<FluxAppelProps>>;
}

export const FLUX_APPEL: FluxAppel[] = [
  // IT › Incidents : déclaration (titre à saisir, description = origine de l'appel).
  {
    ressource: 'it.demandes.incidents', libelle: 'service.telephonie.affectation.creer.incident', icone: 'ticket',
    composant: React.lazy(() => import('./it/demandes/IncidentDepuisAppel')),
  },
  // Commerce › Pistes : entreprise et contact du correspondant, téléphone, besoin = origine de l'appel.
  {
    ressource: RESSOURCE_PISTES, libelle: 'service.telephonie.affectation.creer.piste', icone: 'pin',
    composant: React.lazy(() => import('./commerce/prospection/PisteDepuisAppel')),
  },
  // Commerce › Affaires : client final = le tiers reconnu.
  {
    ressource: RESSOURCE_AFFAIRES, libelle: 'service.telephonie.affectation.creer.affaire', icone: 'portefeuille', tiersRequis: true,
    composant: React.lazy(() => import('./commerce/vente/AffaireDepuisAppel')),
  },
  // Référentiels › Contacts : nouvelle personne du tiers reconnu, avec le numéro de l'appel.
  {
    ressource: 'contacts', libelle: 'service.telephonie.affectation.creer.contact', icone: 'user', tiersRequis: true,
    composant: React.lazy(() => import('./referentiels/ContactDepuisAppel')),
  },
];
